import { Component } from '@theme/component';

/**
 * @typedef {Object} ExpandableRefs
 * @property {HTMLElement} expandableContent - The clipped content container
 * @property {HTMLButtonElement} expandableButton - The button that toggles the clip
 */

/**
 * Reveals content that is clipped to a fixed height behind a fade.
 *
 * The theme's `show-more-component` cannot do this job: it toggles a `hidden`
 * class on a list of discrete child items, whereas here a single element needs
 * its height released. Everything else (the fade, the collapsed height) is CSS —
 * this only animates between the two heights and flips `data-expanded`.
 *
 * @extends {Component<ExpandableRefs>}
 */
class ChExpandable extends Component {
  requiredRefs = ['expandableContent', 'expandableButton'];

  /** @type {Animation | undefined} */
  #animation;

  /** @type {number} */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();

    // Nothing to reveal when the content already fits: drop the button so we do
    // not offer an interaction that visibly does nothing.
    requestAnimationFrame(() => this.#syncButtonVisibility());
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver.disconnect();
  }

  #resizeObserver = new ResizeObserver(() => {
    if (this.dataset.expanded === 'true') return;
    this.#syncButtonVisibility();
  });

  #syncButtonVisibility() {
    const { expandableContent, expandableButton } = this.refs;
    if (!expandableContent || !expandableButton) return;

    const overflows = expandableContent.scrollHeight - expandableContent.clientHeight > 1;
    expandableButton.hidden = !overflows;
    this.toggleAttribute('data-clipped', overflows);
  }

  /**
   * Expands or collapses the content.
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const { expandableContent, expandableButton } = this.refs;
    if (!expandableContent) return;

    const expanding = this.dataset.expanded !== 'true';
    const startHeight = expandableContent.clientHeight;

    // Measure the target height with the clip lifted, then put it back so the
    // animation has somewhere to travel from.
    this.dataset.expanded = expanding ? 'true' : 'false';
    expandableButton?.setAttribute('aria-expanded', String(expanding));
    const endHeight = expanding ? expandableContent.scrollHeight : this.#collapsedHeight;

    if (expanding) this.#collapsedHeight = startHeight;

    this.#animation?.cancel();
    this.#animation = expandableContent.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      expandableContent.style.removeProperty('height');
    };
  };

  /** @type {number} */
  #collapsedHeight = 0;
}

if (!customElements.get('ch-expandable')) {
  customElements.define('ch-expandable', ChExpandable);
}
